import { useState, useEffect, useCallback } from "react";
import { useToast } from '../../../../shared/toastMessages/toastProvider';
import { Typography, TextField, Stack, Paper } from '@mui/material';

import TableComponent from "../../../../shared/table/table";
import getApiData from '../../../../../hooks/apiRequests/getRequest';
import postApiData from '../../../../../hooks/apiRequests/postsRequest';
import putApiData from '../../../../../hooks/apiRequests/putRequest';
import deleteApiData from "../../../../../hooks/apiRequests/deleteRequest";
import ModalComponent from "../../../../shared/modal/modal";
import { normalizeDomain, validateDomain } from "./domainValidation";
import { normalizePageParam } from "../../../../../hooks/pagination";

import { theme } from '../../../../../configs/theme';

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const Domains = () => {
  const [data, setData] = useState();
  const [progress, setProgress] = useState(false);
  const [domain, setDomain] = useState("");
  const [domainError, setDomainError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState("1");

  const { showToast } = useToast();

  const fetchDomains = useCallback(async () => {
    setProgress(true);
    try {
      const response = await getApiData(
        `/List/Domains?Search_filter=${encodeURIComponent(query)}&page_number=${page}`
      );
      if (!response?.data) {
        throw new Error("Empty domains list response");
      }
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Failed to load data", "error");
    } finally {
      setProgress(false);
    }
  }, [query, page, showToast]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const newpage = async (nextPage, search) => {
    setQuery(search || "");
    setPage(normalizePageParam(nextPage));
  };

  const resetDomainForm = () => {
    setDomain("");
    setDomainError("");
    setSelectedId(null);
  };

  const handleDomainChange = (value) => {
    setDomain(value);
    if (domainError) setDomainError("");
  };

  const getValidatedDomain = () => {
    const result = validateDomain(domain);
    if (!result.ok) {
      setDomainError(result.error);
      return null;
    }
    setDomainError("");
    return result.domain;
  };

  const addDomain = async () => {
    const validatedDomain = getValidatedDomain();
    if (!validatedDomain) return;

    const body = {
      id: 0,
      domain: validatedDomain,
      time_created: new Date().toISOString(),
    };

    try {
      const response = await postApiData("/Create/Domain", body);
      if (response?.error || !response?.data) {
        showToast("Failed to add domain", "error");
        return;
      }
      showToast("Added new domain successfully", "success");
      setOpenAdd(false);
      resetDomainForm();
      setPage("1");
      await fetchDomains();
    } catch (error) {
      console.error("Error adding domain:", error);
      showToast("Unable to add domain", "error");
    }
  };

  const editDomain = async () => {
    const validatedDomain = getValidatedDomain();
    if (!validatedDomain) return;

    const body = { id: selectedId, domain: validatedDomain };

    try {
      const response = await putApiData("/Update/Domain", body);
      if (response?.data) {
        showToast("Updated domain successfully", "success");
        setOpenEdit(false);
        resetDomainForm();
        await fetchDomains();
      } else {
        showToast("Failed to update domain", "error");
      }
    } catch (error) {
      console.error("Error updating domain:", error);
      showToast("Unable to update domain", "error");
    }
  };

  const DeleteDomain = async () => {
    try {
      const response = await deleteApiData(`/Delete/Domain?DomainId=${selectedId}`);
      if (!response) {
        showToast("Failed to delete Domain", "error");
        return;
      }
      showToast("Domain deleted successfully", "success");
      setOpenDelete(false);
      setSelectedId(null);
      await fetchDomains();
    } catch (error) {
      console.error("Error deleting Domain:", error);
      showToast("Unable to delete Domain", "error");
    }
  };

  const handleAdd = () => {
    if (openAdd) {
      setOpenAdd(false);
      resetDomainForm();
      return;
    }
    resetDomainForm();
    setOpenAdd(true);
  };

  const handleEdit = (id) => {
    const item = data?.data.find((x) => x.id === id);
    setSelectedId(id);
    setDomain(normalizeDomain(item?.domain || ""));
    setDomainError("");
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    resetDomainForm();
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDelete(true);
  };

  return (
    <>
      <Paper elevation={0} sx={cardSx}>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Blocked domains
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Domains that should be excluded from mailing and email discovery.
          </Typography>
        </Stack>

        <TableComponent
          apiData={data}
          progress={progress}
          noResults={!progress && Array.isArray(data?.data) && data.data.length === 0}
          columnNames={{
            domain: "Domain",
            time_created: "Time Created",
          }}
          handleAdd={handleAdd}
          handleOpen={handleEdit}
          handleSpecDelete={handleDeleteClick}
          theme={theme}
          newpage={newpage}
          query={query}
          pageNumber={page}
        />
      </Paper>

      <ModalComponent
        title={"Add Blocked Domain"}
        subtitle="Enter a domain with a known suffix to block it from mailing."
        open={openAdd}
        handleOpen={handleAdd}
        theme={theme}
        handleSubmit={addDomain}
        alternateSaveMessage="Add domain"
      >
        <TextField
          label="Domain"
          variant="outlined"
          fullWidth
          size="small"
          placeholder="e.g. example.com"
          value={domain}
          onChange={(e) => handleDomainChange(e.target.value)}
          error={Boolean(domainError)}
          helperText={domainError || "Must include a known suffix such as .com, .co.uk, or .org"}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </ModalComponent>

      <ModalComponent
        title={"Edit Blocked Domain"}
        subtitle="Update the blocked domain. It must still use a known suffix."
        open={openEdit}
        handleOpen={handleCloseEdit}
        theme={theme}
        handleSubmit={editDomain}
        alternateSaveMessage="Save changes"
      >
        <TextField
          label="Domain"
          variant="outlined"
          fullWidth
          size="small"
          placeholder="e.g. example.com"
          value={domain}
          onChange={(e) => handleDomainChange(e.target.value)}
          error={Boolean(domainError)}
          helperText={domainError || "Must include a known suffix such as .com, .co.uk, or .org"}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </ModalComponent>

      <ModalComponent
        title={"Confirm Delete"}
        subtitle="This will permanently remove the blocked domain."
        open={openDelete}
        handleOpen={() => setOpenDelete(false)}
        theme={theme}
        handleSubmit={DeleteDomain}
        alternateSaveMessage="Delete domain"
        submitColor="error"
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete this blocked domain?
        </Typography>
      </ModalComponent>
    </>
  );
};

export default Domains;
